/**
 * Gestão: monta os cartões a partir das EDITORIAS do tenant, um por destino.
 *
 * Antes a tela tinha quatro editorias e os IDs de categoria de um cliente
 * gravados no código. Agora tudo vem de `/editorias`: cada editoria diz em quais
 * sites publica (`pub.sites`) e em qual categoria (`pub.categoria_id`). Nenhum
 * número daqui é fixo.
 */
import type { Editoria } from '@/services/editorias';

export interface CartaoGestao {
  /** Estável entre renderizações: editoria + destino. */
  chave: string;
  editoria: Editoria;
  /** Slug do site de destino; nulo quando a editoria ainda não tem destino. */
  site: string | null;
  /** Categoria do destino no WordPress; nulo quando a editoria não definiu uma. */
  categoriaId: number | null;
}

/** Um cartão por editoria × destino, na ordem das editorias. Sem destino, um cartão sem site. */
export function cartoesDaGestao(editorias: Editoria[]): CartaoGestao[] {
  const ordenadas = [...editorias].sort(
    (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0) || a.nome.localeCompare(b.nome, 'pt-BR'),
  );
  const cartoes: CartaoGestao[] = [];
  for (const editoria of ordenadas) {
    const categoriaId =
      typeof editoria.pub.categoria_id === 'number' && editoria.pub.categoria_id > 0
        ? editoria.pub.categoria_id
        : null;
    const sites = [...new Set(editoria.pub.sites ?? [])];
    if (sites.length === 0) {
      cartoes.push({ chave: `${editoria.slug}:`, editoria, site: null, categoriaId });
      continue;
    }
    for (const site of sites) {
      cartoes.push({ chave: `${editoria.slug}:${site}`, editoria, site, categoriaId });
    }
  }
  return cartoes;
}

/** Quantos destinos distintos recebem alguma editoria. */
export function contarDestinos(cartoes: CartaoGestao[]): number {
  return new Set(cartoes.map((c) => c.site).filter((s): s is string => s !== null)).size;
}
